import type { Mode, Plan, RouteSearch, Variant } from '@trippath/shared';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  byRoad,
  kinAlong,
  kinRoads,
  type MapData,
  pairs,
  planMap,
  planRoads,
  routeMap,
  variantMap,
} from '../../lib/map';
import { MapPanel } from '../../components/map/MapPanel';
import { useRoads, wayAsk } from '../../lib/roads';
import { useGround } from '../../lib/ground';
import { journeysOf } from '../../lib/sky';
import type { Moment } from '../../lib/moment';
import type { Follow, MapPlace } from '../../components/map/TripMap';

/** What the planner's widgets cover on a wide screen: the route is fitted into the rest. */
const COVERED = { top: 290, right: 340, bottom: 80, left: 400 };

/** No variants shown: one list, so the map is not drawn again for a new empty one. */
const NONE: readonly Variant[] = [];

interface Props {
  plan: Plan;
  /** Widgets cover the map's edges: fit the route into the middle, and zoom freely. */
  covered?: boolean;
  /** A route searched and not added yet, drawn dashed, and brought into view. */
  draft?: RouteSearch | null;
  /** The way of each of the draft's legs: by bus or car it is drawn along its road. */
  draftModes?: readonly Mode[];
  /** The variants shown, drawn beside the plan and brought into view. */
  variants?: readonly Variant[];
  /** The stop picked, to fly to. */
  focus?: { lat: number; lon: number; n?: number } | null;
  /** Told where the view settles; the view is then the person's, kept as data changes. */
  onView?: (center: { lat: number; lon: number }) => void;
  /** Told a place name clicked on the map. */
  onPlace?: (place: MapPlace) => void;
  /** Told the stop whose way on was clicked. */
  onLine?: (stop: string) => void;
  /** The way picked, its widget open: it stands out on the map. */
  way?: string | null;
  /** Another trip of the family picked: its way drawn beside the plan's. */
  kin?: MapData | null;
  /** The moment on the timeline: the night then, and whoever is on their way then. */
  moment?: Moment | null;
  /** Told a booked stay pressed on the map, by its booking. */
  onStay?: (booking: string) => void;
  /** A place pressed whose box is open: told where it is as the map moves. */
  follow?: Follow | null;
}

/** The trip on the map: each stop with a place, and the way on from each, by how. */
export function PlanMap({
  plan,
  covered = false,
  draft = null,
  draftModes = [],
  variants = NONE,
  focus = null,
  onView,
  onPlace,
  onLine,
  way = null,
  kin = null,
  moment = null,
  onStay,
  follow = null,
}: Props) {
  const { t } = useTranslation();
  const journeys = useMemo(() => journeysOf(plan), [plan]);
  const sky = useMemo(
    () => (moment === null ? null : { moment, journeys }),
    [moment, journeys],
  );
  const ways = useMemo(
    () => [
      ...planRoads(plan),
      ...(draft?.legs ?? []).flatMap((leg, at) =>
        byRoad(draftModes[at]) ? [wayAsk(leg.from, leg.to, draftModes[at])] : [],
      ),
      ...variants.flatMap((variant) =>
        pairs(variant.places).flatMap(([from, to], at) => {
          const mode = variant.modes[at];
          return byRoad(mode) ? [wayAsk(from, to, mode)] : [];
        }),
      ),
      // Another trip's ferries, buses and trains along their roads too.
      ...kinRoads(kin),
    ],
    [plan, draft, draftModes, variants, kin],
  );
  const roads = useRoads(ways);
  const drawn = useMemo(() => {
    // Each way of the plan says what it is as it is pointed at.
    const parts: MapData[] = [
      planMap(plan, roads, (from, to) =>
        from.out
          ? t('plan.way', { mode: t(`mode.${from.out.mode}`), from: from.city, to: to.city })
          : `${from.city} → ${to.city}`,
      ),
    ];
    for (const variant of variants) parts.push(variantMap(variant, roads));
    if (kin !== null) parts.push(kinAlong(kin, roads));
    if (draft !== null) {
      const searched = routeMap(draft, draftModes, roads);
      parts.push({
        points: searched.points,
        lines: searched.lines.map((line) => ({ ...line, kind: 'draft' as const })),
      });
    }
    return {
      points: parts.flatMap((part) => part.points),
      lines: parts.flatMap((part) => part.lines),
    };
  }, [plan, draft, draftModes, variants, roads, t, kin]);
  // The plan's ways on the ground by the ground's height, once it is known.
  const lines = useGround(drawn.lines);
  const data = useMemo(() => ({ ...drawn, lines }), [drawn, lines]);
  // The draft, or else the variant shown, is brought into view when it is drawn where the
  // person is not looking.
  const show = useMemo(() => {
    if (draft !== null) return routeMap(draft).points;
    return variants.length === 0 ? null : variants.flatMap((variant) => variant.places);
  }, [draft, variants]);
  // The way picked fills the view: its two ends, its airports when known, else its stops.
  const fit = useMemo(() => {
    // One with its own track: all of the track, as it bends.
    const tracked = plan.stops.find((stop) => stop.id === way)?.track;
    if (tracked) return tracked.path.map(([lon, lat]) => ({ lat, lon }));
    const ends = plan.stops.flatMap((stop, at) =>
      stop.id === way
        ? [stop.out?.from ?? stop.place, stop.out?.to ?? plan.stops[at + 1]?.place]
        : [],
    );
    const points = ends.flatMap((end) => (end ? [{ lat: end.lat, lon: end.lon }] : []));
    // Another trip's way picked: its line's two ends.
    const line = kin?.lines.find((one) => one.stop === way);
    const kinEnds = [line?.path[0], line?.path.at(-1)].flatMap((end) =>
      end ? [{ lon: end[0], lat: end[1] }] : [],
    );
    const all = points.length > 0 ? points : kinEnds;
    return all.length === 0 ? null : all;
  }, [plan, way, kin]);
  const missing = plan.stops.filter((stop) => stop.place === null).map((stop) => stop.city);
  return (
    <section className="panel plan-map" aria-labelledby="map-title">
      <h2 id="map-title">{t('map.title')}</h2>
      <MapPanel
        data={data}
        // Left for another of the trip's pages and come back to: where it was.
        viewKey={`plan:${plan.trip.id}`}
        sky={sky}
        padding={covered ? COVERED : undefined}
        focus={focus}
        show={show}
        fit={fit}
        cooperative={!covered}
        keepView={onView !== undefined}
        onView={onView}
        onPlace={onPlace}
        onLine={onLine}
        onPoint={onStay}
        way={way}
        follow={follow}
        label={
          data.points.length === 0
            ? t('map.empty')
            : t('map.label', { places: data.points.map((p) => p.label).join(' → ') })
        }
      />
      {missing.length > 0 && (
        <p className="quiet">{t('map.missing', { cities: missing.join(', ') })}</p>
      )}
    </section>
  );
}
